import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import PromoCodesTemplate from "templates/promo-codes";
import Loader from "components/Atoms/SharedComponents/Loader/Loader";
import {
  createPromoCodeRequest,
  deletePromoCodeRequest,
  getPromoCodesRequest,
  getPromoCodeDetailsRequest,
  updatePromoCodeRequest,
} from "services/modules/promo-codes";

import { toast } from "react-toastify";

function PromoCodesPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  let [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const type = searchParams.get("type");

  const list = useSelector((state) => state.promoCodes.promoCodes);
  const count = useSelector((state) => state.promoCodes.count);
  const details = useSelector((state) => state.promoCodes.promoCode);
  const load = useSelector((state) => state.promoCodes.load);

  //permissions
  const currentUser = useSelector(
    (state) => state.authenticate.currentUserProfile,
  );
  // Assuming promo_codes permission key, or just restrict to super_admin for now
  const isSuperAdmin = currentUser?.privilege == "super_admin";

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [page, setPage] = useState(1);
  const [filterObj, setFilterObj] = useState({ only_deleted: false }); // Default Active

  const handlePageChange = (page) => {
    setPage(page);
    setFilterObj((prev) => {
      return { ...prev, page_number: page };
    });
  };

  const getDetails = async () => {
    dispatch(getPromoCodeDetailsRequest({ id }));
  };

  const getList = async (filterObj) => {
    const requestData = {
      params: {
        page_size: 10,
        page_number: 1,
        only_deleted: false, // Default fallback
        ...filterObj,
      },
    };
    dispatch(getPromoCodesRequest(requestData));
  };

  const rowData = (data) => {
    setSelectedRow(data);
  };

  const breadcrumbs = !type
    ? [
        {
          active: true,
          path: "/",
          title: t("common.home"),
        },
        {
          active: false,
          path: "/promo-codes",
          title: t("promo_codes.title"),
        },
      ]
    : [
        {
          active: true,
          path: "/",
          title: t("common.home"),
        },
        {
          active: true,
          path: "/promo-codes",
          title: t("promo_codes.title"),
        },
        {
          active: false,
          title:
            type === "details"
              ? t("promo_codes.promo_code_details")
              : id
                ? t("promo_codes.edit_promo_code")
                : t("promo_codes.add_promo_code"),
        },
      ];

  const handelSubmit = (data, formik) => {
    const action = () => {
      searchParams.delete("id");
      searchParams.delete("type");
      navigate("/promo-codes");
      !id && formik?.resetForm();
      toast.success(
        id ? t("promo_codes.promo_code_updated") : t("promo_codes.promo_code_created"),
      );
    };

    // Format payload as expected: { promo_code: { ... } }
    const requestData = {
      body: {
        promo_code: {
          ...data,
        },
      },
      action,
    };
    if (id) {
      //update call
      dispatch(updatePromoCodeRequest({ ...requestData, id }));
    } else {
      //create call
      dispatch(createPromoCodeRequest(requestData));
    }
  };

  const filterList = [
    {
      type: "filter",
      placeholder: t("promo_codes.status"),
      defaultValue: { id: false, name: t("promo_codes.active") },
      onChange: (value) => {
        setFilterObj((s) => {
          return { ...s, only_deleted: value?.id, page_number: 1 };
        });
      },
      options: [
        { id: false, name: t("promo_codes.active") },
        { id: true, name: t("promo_codes.deleted") },
      ],
    },
  ];

  const handleDelete = (id) => {
    //action to be called after successfull deletion
    const action = () => {
      //if list.length == 1 & page !== 1 -> setPage(page-1) & fetch
      if ((list.length === 1) & (page !== 1)) {
        setPage(page - 1);
        setFilterObj({
          ...filterObj,
          page_number: page - 1,
        });
        getList({ ...filterObj, page_number: page - 1 });
      } else {
        getList(filterObj);
      }
      type === "details" && navigate("/promo-codes");
      toast.success(t("promo_codes.promo_code_deleted"));
    };
    const requestData = {
      id,
      action, //dispatched on success
    };
    dispatch(deletePromoCodeRequest(requestData));
    setOpenDeleteModal(false);
  };

  //list of actions to be displayed when clicked in the 3 dots
  const actionsList = ({ index, data }) => {
    return [
      {
        condition: () => {
          return isSuperAdmin; // Or specific permission
        },
        click: () => {
          navigate(`?type=form&id=${data?.id}`);
        },
        text: t("common.edit"),
      },
      {
        condition: () => {
          return isSuperAdmin;
        },
        click: () => {
          setSelectedRow(data);
          setOpenDeleteModal(true);
        },
        text: t("common.delete"),
      },
    ];
  };

  //if you need more buttons in the header beside the add button
  const headerChildren = <></>;

  //if more modals needed, pass them to the modalComponents as below
  const modalComponents = [];

  // listing all promo codes
  useEffect(() => {
    if (filterObj) getList(filterObj);
  }, [filterObj]);

  // details of a single promo code
  useEffect(() => {
    if (id) getDetails();
  }, [id]);

  return (
    <>
      <Loader open={load?.loading} />
      <PromoCodesTemplate
        filterObj={filterObj}
        setFilterObj={setFilterObj}
        data={list}
        count={count}
        paginationPage={page}
        handlePageChange={handlePageChange}
        rowData={rowData}
        details={details}
        breadcrumbs={breadcrumbs}
        filterList={filterList}
        handelSubmit={handelSubmit}
        actionsList={actionsList}
        headerChildren={headerChildren}
        openDeleteModal={openDeleteModal}
        setOpenDeleteModal={setOpenDeleteModal}
        handleDeleteRequest={handleDelete}
        modalComponents={modalComponents}
        formChildren={<></>}
        can_create={isSuperAdmin}
      />
    </>
  );
}

export default PromoCodesPage;
